import { ROOM_TYPES, ROOM_TYPE_LABELS, type RoomType } from "@ckgo2/shared/types";
import { Plus, Trash2, BedDouble } from "lucide-react";

export interface RoomTypeVariant {
  id?: string;
  roomType: RoomType;
  cost: number;
  currency?: string | null;
  sortOrder?: number;
}

interface Props {
  value: RoomTypeVariant[];
  onChange: (variants: RoomTypeVariant[]) => void;
  /** Component currency — shown as the per-room cost suffix (variants inherit it when unset). */
  currency?: string;
}

/**
 * Room-type price variants for a supplier component (Block 6.1 Řez 2b). A hotel component lists one
 * row per offered room type with its PER-ROOM buy cost; picking a room type on a service booking
 * snapshots that cost, and the calculation engine splits it across the room's occupancy. Follows
 * the SocialReelsEditor list-editor pattern — onChange always emits the full array.
 */
export default function RoomTypeVariantsEditor({ value, onChange, currency = "" }: Props) {
  const variants = value || [];

  const add = () => {
    // First room type not already used (keeps the list tidy). Once all types are used there's
    // nothing valid to add — no-op rather than appending a duplicate (the button is hidden then).
    const used = new Set(variants.map((v) => v.roomType));
    const next = ROOM_TYPES.find((t) => !used.has(t));
    if (!next) return;
    onChange([
      ...variants,
      { roomType: next, cost: 0, currency: null, sortOrder: variants.length },
    ]);
  };
  const remove = (i: number) =>
    onChange(variants.filter((_, idx) => idx !== i).map((v, idx) => ({ ...v, sortOrder: idx })));
  const update = (i: number, patch: Partial<RoomTypeVariant>) => {
    const next = [...variants];
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };

  return (
    <div className="space-y-3">
      <div>
        <label className="label flex items-center gap-1.5">
          <BedDouble className="h-4 w-4" /> Room types
        </label>
        <p className="text-xs text-gray-400">
          Per-room buy cost for each offered room type. Used for accommodation priced by room — a
          booking picks one type and snapshots its cost; the calculator splits it across occupancy
          (single → no roommate, the supplement falls out). Leave empty to use the flat cost above.
        </p>
      </div>

      {variants.length > 0 && (
        <div className="space-y-2">
          {variants.map((v, i) => {
            // Hide types already used by OTHER rows so two rows can't collide (the unique
            // (component, room_type) index would otherwise reject the save).
            const usedByOthers = new Set(
              variants.filter((_, idx) => idx !== i).map((x) => x.roomType),
            );
            return (
              <div key={v.id ?? i} className="flex items-end gap-2">
                <div className="flex-1">
                  {i === 0 && <label className="label text-xs">Type</label>}
                  <select
                    className="input"
                    value={v.roomType}
                    onChange={(e) => update(i, { roomType: e.target.value as RoomType })}
                  >
                    {ROOM_TYPES.filter((t) => t === v.roomType || !usedByOthers.has(t)).map((t) => (
                      <option key={t} value={t}>
                        {ROOM_TYPE_LABELS[t]}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="w-36">
                  {i === 0 && (
                    <label className="label text-xs">
                      Cost / room{currency ? ` (${currency})` : ""}
                    </label>
                  )}
                  <input
                    type="number"
                    min={0}
                    className="input"
                    value={v.cost}
                    onChange={(e) => update(i, { cost: Number(e.target.value) })}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => remove(i)}
                  className="mb-0.5 rounded p-2 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
                  title="Remove room type"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {variants.length < ROOM_TYPES.length && (
        <button type="button" onClick={add} className="btn-secondary btn-sm">
          <Plus className="h-4 w-4" /> Add room type
        </button>
      )}
    </div>
  );
}
